import * as Sentry from "@sentry/browser";
import type {HtmxResponseInfo} from "htmx.org";

type SentryHub = ReturnType<typeof Sentry.getCurrentHub>;
type SentryScope = ReturnType<SentryHub["getScope"]>;
type SentrySpan = ReturnType<SentryScope["getSpan"]>;
type SentryTransaction = ReturnType<typeof Sentry.startTransaction>;

type SentryPortal = "admin" | "agent" | "applicant";

type SentryUser = {
  id?: string;
  session?: string;
  agency_id?: string;
};

type HtmxRequestState = {
  aborted?: boolean;
  cleanupTimer?: number;
  loadEndHandler?: EventListener;
  method: string;
  path: string;
  timedOut?: boolean;
  transaction: SentryTransaction;
};

const HTMX_TRANSACTION_MAX_DURATION_MS = 5 * 60 * 1000;

const configuredTracesSampleRate = Number(process.env.SENTRY_TRACES_SAMPLE_RATE ?? 0.1);
const tracesSampleRate =
  Number.isFinite(configuredTracesSampleRate) && configuredTracesSampleRate >= 0 && configuredTracesSampleRate <= 1
    ? configuredTracesSampleRate
    : 0.1;

const portal = document.querySelector<HTMLMetaElement>('meta[name="sentry-portal"]')?.content as
  | SentryPortal
  | undefined;
const dsn = process.env.SENTRY_DSN;
const sentryEnabled = !!dsn && !!portal && ["production", "staging"].includes(process.env.TARGET_ENV);

Sentry.init({
  dsn,
  enabled: sentryEnabled,
  environment: process.env.TARGET_ENV,
  release: process.env.GIT_SHA || undefined,
  sendDefaultPii: false,
  tracesSampleRate,
  tracePropagationTargets: [
    /^\//,
    /^https:\/\/(?:[a-z0-9-]+\.)?scraye\.com(?::\d+)?\//,
    /^http:\/\/localhost(?::\d+)?\//,
  ],
  integrations: [new Sentry.BrowserTracing()],
  initialScope: portal ? {tags: {portal}} : undefined,
});

const userMetadata = document.querySelector<HTMLMetaElement>('meta[name="sentry-user"]')?.content;
if (userMetadata) {
  try {
    const user = JSON.parse(userMetadata) as SentryUser;
    if (user.id || user.session || user.agency_id) {
      Sentry.setUser(user);
    }
  } catch (error) {
    Sentry.captureException(error, {tags: {source: "sentry_user_metadata"}});
  }
}

if (sentryEnabled) {
  const requests = new WeakMap<XMLHttpRequest, HtmxRequestState>();
  const htmxTransactions = new WeakSet<SentryTransaction>();
  const activeTransactions: Array<SentryTransaction> = [];
  let parentSpan: SentrySpan;

  document.addEventListener("htmx:beforeSend", (event) => {
    const {xhr, requestConfig, pathInfo} = (event as CustomEvent<HtmxResponseInfo>).detail;
    if (requests.has(xhr)) {
      return;
    }

    const method = requestConfig?.verb?.toUpperCase() || "GET";
    const path = normalizeRequestPath(pathInfo?.finalRequestPath || requestConfig?.path || "/");
    const scope = Sentry.getCurrentHub().getScope();
    if (!activeTransactions.length) {
      parentSpan = scope.getSpan();
    }

    const transaction = Sentry.startTransaction({
      name: `${method} ${path}`,
      op: "ui.htmx",
      trimEnd: true,
      data: {
        "http.method": method,
        "http.url": path,
      },
    });

    const state: HtmxRequestState = {method, path, transaction};
    requests.set(xhr, state);
    htmxTransactions.add(transaction);
    activeTransactions.push(transaction);

    state.loadEndHandler = () => {
      queueMicrotask(() => completeHtmxRequest(state, xhr));
    };
    xhr.addEventListener("loadend", state.loadEndHandler, {once: true});
    state.cleanupTimer = window.setTimeout(() => {
      state.timedOut = true;
      completeHtmxRequest(state, xhr);
    }, HTMX_TRANSACTION_MAX_DURATION_MS);

    // HTMX sends the XHR immediately after this event. Making the transaction
    // active lets BrowserTracing create the request span and propagate its trace.
    scope.setSpan(transaction);
  });

  document.addEventListener("htmx:responseError", (event) => {
    const {xhr} = (event as CustomEvent<HtmxResponseInfo>).detail;
    const state = requests.get(xhr);
    if (state && xhr.status >= 500) {
      captureHtmxFailure(state, xhr, "server_error");
    }
  });

  document.addEventListener("htmx:sendError", (event) => {
    const {xhr} = (event as CustomEvent<HtmxResponseInfo>).detail;
    const state = requests.get(xhr);
    if (state) {
      captureHtmxFailure(state, xhr, "network_error");
    }
  });

  document.addEventListener("htmx:timeout", (event) => {
    const {xhr} = (event as CustomEvent<HtmxResponseInfo>).detail;
    const state = requests.get(xhr);
    if (state) {
      state.timedOut = true;
      captureHtmxFailure(state, xhr, "timeout");
    }
  });

  document.addEventListener("htmx:sendAbort", (event) => {
    const {xhr} = (event as CustomEvent<HtmxResponseInfo>).detail;
    const state = requests.get(xhr);
    if (state) {
      state.aborted = true;
    }
  });

  document.addEventListener("htmx:onLoadError", (event) => {
    const {error, xhr} = (event as CustomEvent<HtmxResponseInfo & {error: unknown}>).detail;
    const state = requests.get(xhr);
    if (!state) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setSpan(state.transaction);
      scope.setTag("htmx.failure", "response_processing_error");
      Sentry.captureException(error);
    });
    completeHtmxRequest(state, xhr);
  });

  function completeHtmxRequest(state: HtmxRequestState, xhr: XMLHttpRequest) {
    if (requests.get(xhr) !== state) {
      return;
    }
    finishHtmxRequest(state, xhr);
    requests.delete(xhr);
  }

  function finishHtmxRequest(state: HtmxRequestState, xhr: XMLHttpRequest) {
    if (state.cleanupTimer !== undefined) {
      window.clearTimeout(state.cleanupTimer);
    }
    if (state.loadEndHandler) {
      xhr.removeEventListener("loadend", state.loadEndHandler);
    }
    if (xhr.status) {
      state.transaction.setHttpStatus(xhr.status);
    } else if (state.aborted) {
      state.transaction.setStatus("cancelled");
    } else if (state.timedOut) {
      state.transaction.setStatus("deadline_exceeded");
    } else {
      state.transaction.setStatus("unknown_error");
    }
    state.transaction.finish();

    const index = activeTransactions.indexOf(state.transaction);
    if (index !== -1) {
      activeTransactions.splice(index, 1);
    }

    const scope = Sentry.getCurrentHub().getScope();
    const currentSpan = scope.getSpan();
    if (currentSpan && htmxTransactions.has(currentSpan as SentryTransaction)) {
      scope.setSpan(activeTransactions.at(-1) || (parentSpan?.endTimestamp ? undefined : parentSpan));
    }
    if (!activeTransactions.length) {
      parentSpan = undefined;
    }
  }
}

function captureHtmxFailure(state: HtmxRequestState, xhr: XMLHttpRequest, failure: string) {
  Sentry.withScope((scope) => {
    scope.setSpan(state.transaction);
    scope.setTags({
      "htmx.failure": failure,
      "http.method": state.method,
      "http.status_code": xhr.status,
    });
    scope.setFingerprint(["htmx", state.method, state.path, String(xhr.status), failure]);
    scope.setContext("htmx_request", {
      method: state.method,
      path: state.path,
      status: xhr.status,
    });
    Sentry.captureMessage("HTMX request failed", "error");
  });
}

function normalizeRequestPath(requestPath: string) {
  let pathname: string;
  try {
    pathname = new URL(requestPath, window.location.origin).pathname;
  } catch {
    pathname = "/";
  }

  return pathname
    .split("/")
    .map((segment) => {
      const isIdentifier =
        /^\d+$/.test(segment) ||
        /^[a-f\d]{24}$/i.test(segment) ||
        /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(segment) ||
        segment.length > 32;
      return isIdentifier ? ":id" : segment;
    })
    .join("/");
}
